<template>
  <div class="synthese__container">
    <SyntheseSection
      title="L'employeur"
      :items="employerDetails"
    />

    <SyntheseSection
      title="Salarié(e)"
      :items="salariesDetails"
    >
      <template #header>
        <div class="element__header">
          <BasicText
            size="body-m"
            color="secondary-900"
            weight="bold"
          >
            Nom/Prénom :
          </BasicText>
          <BasicText
            size="body-m"
            color="secondary-900"
            weight="bold"
          >
            Date de naissance :
          </BasicText>
          <BasicText
            size="body-m"
            color="secondary-900"
            weight="bold"
          >
            Nature du contrat :
          </BasicText>
        </div>
      </template>
      <template #items>
        <div
          v-for="(salarie, index) in salariesDetails"
          :key="index"
          class="element__row"
        >
          <BasicText
            size="body-m"
            color="secondary-900"
          >
            {{ salarie['Nom/Prénom'] ?? '' }}
          </BasicText>
          <BasicText
            size="body-m"
            color="secondary-900"
          >
            {{ salarie['Date de naissance'] ?? '' }}
          </BasicText>
          <BasicText
            size="body-m"
            color="secondary-900"
          >
            {{ salarie['Nature du contrat'] ?? '' }}
          </BasicText>
        </div>
      </template>
    </SyntheseSection>
    <SyntheseSection
      v-for="(formationInterne, index) in formationsInterne"
      :key="index"
      title="Formation (interne)"
      :items="formationInterne"
    />
    <SyntheseSection
      v-for="(formationExterne, index) in formationsExterne"
      :key="index"
      title="Formation (externe)"
      :items="formationExterne"
    />
    <SyntheseSection
      title="Chiffrage (montants HT)"
      :items="chiffrageDetails"
    />
  </div>
  <FormContainer>
    <template #content>
      <WrapperCheckbox
        id="field_unknown1"
        v-model="attestationSurHonneurModel"
        :alertLabel="meta.attestationSurHonneur?.$messageErreurDirty!"
        :required="meta.attestationCadreInterventionFse?.$status === PropertyStatus.Required"
        label="J’atteste sur l’honneur l’exactitude des données saisies."
        :readonly
        class="col-36"
      />
      <WrapperCheckbox
        id="field_unknown2"
        v-model="acceptationCgModel"
        :alertLabel="meta.acceptationCg?.$messageErreurDirty!"
        :required="meta.attestationCadreInterventionFse?.$status === PropertyStatus.Required"
        :readonly
        class="col-36"
      >
        <BasicText style="flex: 1">
          J'accepte les Conditions Générales (CG) d'Akto et les conditions de prise en charge, et je confirme avoir pris
          connaissance de celles-ci via le lien suivant : «
          <BasicLink
            label="Conditions générales - Akto"
            @link-click="redirectExterne('https://www.akto.fr/conditions-generales')"
          />
        </BasicText>
        »
      </WrapperCheckbox>
      <!-- Attestations FSE -->
      <WrapperCheckbox
        v-if="meta.attestationCadreInterventionFse?.$status !== PropertyStatus.NotPresent"
        v-model="attestationCadreInterventionFseModel"
        :alertLabel="meta.attestationCadreInterventionFse?.$messageErreurDirty!"
        :required="meta.attestationCadreInterventionFse?.$status === PropertyStatus.Required"
        :readonly
        class="col-36"
      >
        <BasicText style="flex: 1">
          J'atteste avoir pris connaissance du
          <BasicLink
            label="cadre d'intervention du financement FSE+"
            @link-click="redirectExterne('https://www.akto.fr/beneficier-dune-aide-du-fse-en-2025-comment-faire/')"
          />
          et je m'engage à le respecter.
        </BasicText>
      </WrapperCheckbox>
      <WrapperCheckbox
        v-if="meta.attestationEngagementsFse?.$status !== PropertyStatus.NotPresent"
        v-model="attestationEngagementsFseModel"
        :alertLabel="meta.attestationEngagementsFse?.$messageErreurDirty!"
        :required="meta.attestationEngagementsFse?.$status === PropertyStatus.Required"
        :readonly
        class="col-36"
      >
        <BasicText style="flex: 1">
          J'atteste avoir pris connaissance des
          <BasicLink
            label="engagements relatifs au financement FSE+"
            @link-click="
              redirectExterne('https://www.akto.fr/content/uploads/2025/01/FSE_Memo-engagements-Entreprise-et-OF.pdf')
            "
          />
          et je m'engage à les respecter.
        </BasicText>
      </WrapperCheckbox>
      <WrapperCheckbox
        v-if="meta.attestationInformationParticipantsFse?.$status !== PropertyStatus.NotPresent"
        v-model="attestationInformationParticipantsFseModel"
        :alertLabel="meta.attestationInformationParticipantsFse?.$messageErreurDirty!"
        :required="meta.attestationInformationParticipantsFse?.$status === PropertyStatus.Required"
        :readonly
        class="col-36"
      >
        <BasicText style="flex: 1">
          Je m'engage à informer les participants du cofinancement par le programme Fonds Social Européen + « emploi,
          inclusion, jeunesse et compétences » et à leur faire remplir les questionnaires en ligne
          <BasicLink
            label="d'entrée"
            @link-click="redirectExterne('https://forms.monday.com/forms/6167b1704255258b1a5f8a4bf53fd043?r=euc1')"
          />
          et de
          <BasicLink
            label="sortie"
            @link-click="redirectExterne('https://forms.monday.com/forms/49ba4512fe566d2b91e0234f6c1b0c2c?r=euc1')"
          />
          de formation.
        </BasicText>
      </WrapperCheckbox>
      <WrapperInput
        id="field_unknown"
        v-model="commentaireModel"
        label="Commentaires (infos complémentaires...)"
        :required="meta.commentaire?.$status === PropertyStatus.Required"
        :alertLabel="meta.commentaire?.$messageErreurDirty!"
        class="col-36"
        deletable
        hint="256 caractères maximum"
        :maxlength="256"
        :readonly
      />
    </template>
  </FormContainer>
</template>

<script setup lang="ts">
  import { PropertyStatus } from '@/features/domaine/property-status'
  import { calculSalaire, idccAutoriseTT } from '@/features/domaine/regles-akto/shared'
  import { ModalitePedagogiqueLibelle, NatureContratLibelle } from '@/features/domaine/types/opco/Demande/AF'
  import { useDemandeAFStore } from '@/features/interface/demande/af/useDemandeAFStore'
  import { FormContainer, SyntheseSection } from '@/features/interface/demande/shared'
  import { getLabelBoolean } from '@/features/interface/shared/tools'
  import { dateFormatter, formatNumber, isDate } from '@/features/shared/tools'
  import { redirectExterne } from '@/features/shared/tools/url'
  import { storeToRefs } from 'pinia'
  import { computed } from 'vue'

  const store = useDemandeAFStore()
  const { model } = useDemandeAFStore()
  const { setupFieldWithKey } = useDemandeAFStore()
  const { meta, readonly } = storeToRefs(store)
  const getDateIntitule = (value: any) => (isDate(value) ? dateFormatter(value) : '')
  const idcc = model?.employeur?.etablissement?.idcc?.code ?? ''

  const commentaireModel = setupFieldWithKey<string>('commentaire', true)

  const attestationSurHonneurModel = setupFieldWithKey<boolean>('attestationSurHonneur')
  const acceptationCgModel = setupFieldWithKey<boolean>('acceptationCg')
    
  const attestationCadreInterventionFseModel = setupFieldWithKey<boolean>('attestationCadreInterventionFse')
  const attestationEngagementsFseModel = setupFieldWithKey<boolean>('attestationEngagementsFse')
  const attestationInformationParticipantsFseModel = setupFieldWithKey<boolean>('attestationInformationParticipantsFse')

  const employerDetails = computed(() => ({
    'Entité ADH': model?.employeur?.etablissement?.denomination,
    Siret: model?.employeur?.etablissement?.siret?.valeur,
    IDCC: idcc,
    Email: model?.employeur?.contact?.email,
    'N° et Voie': model?.employeur?.etablissement?.adresse?.voie,
    Complément: model?.employeur?.etablissement?.adresse?.complement,
    'Code Postal': model?.employeur?.etablissement?.adresse?.codePostal,
    Ville: model?.employeur?.etablissement?.adresse?.commune,
  }))

  const salariesDetails = computed(() => {
    return (
      model?.salaries?.map((salarie) => ({
        'Nom/Prénom': `${salarie?.nomNaissance ?? ''} ${salarie?.prenom ?? ''}`,
        'Date de naissance': getDateIntitule(salarie?.dateNaissance) ?? '',
        'Nature du contrat': salarie?.natureContrat ? NatureContratLibelle[salarie?.natureContrat] : '',
      })) ?? []
    )
  })

  type FormationData = {
    [key: string]: string
  }

  const formationsInterne = computed((): FormationData[] => {
    const modules = model?.formation?.modules ?? []

    return modules
      .filter((module) => module.typeModule === 'Interne')
      .map((module) => ({
        'Adresse du lieu de formation': module?.lieuFormation
          ? `${module.lieuFormation.voie ?? ''} ${module.lieuFormation.codePostal ?? ''} ${module.lieuFormation.commune ?? ''}`
          : '',
        'Intitulé de formation': module?.intitule ?? '',
        'Date de début de formation': getDateIntitule(module?.dateDebut),
        'Date de fin de formation': getDateIntitule(module?.dateFin),
        'Nombre d’heure de formation': String(module?.duree ?? ''),
        'Modalité pédagogique': module?.modalitePedagogique
          ? ModalitePedagogiqueLibelle[module.modalitePedagogique]
          : '',
        'Coût formation': `${formatNumber(
          module?.fraisPedagogiques
            ?.filter((x) => x.nature === 'Pedagogique')
            .reduce((acc, frais) => acc + (frais.montant ?? 0), 0),
          { decimal: 2, separateur: true },
        )} €`,
        'Autres types de coûts': `${formatNumber((module?.fraisPedagogiques?.filter((x) => x.nature !== 'Pedagogique').reduce((acc, frais) => acc + (frais.montant ?? 0), 0) ?? 0) + (module?.fraisAnnexesSalarie?.reduce((acc, frais) => acc + (frais.montant ?? 0), 0) ?? 0), { decimal: 2, separateur: true })} €`,
      }))
  })

  const formationsExterne = computed((): FormationData[] => {
    const modules = model?.formation?.modules ?? []

    return modules
      .filter((module) => module.typeModule === 'Externe')
      .map((module) => ({
        'Entité OF': module?.etablissement?.denomination ?? '',
        SIRET:
          typeof module?.etablissement?.siret === 'string'
            ? module.etablissement.siret
            : (module?.etablissement?.siret?.valeur ?? ''),
        'Adresse du lieu de formation': module?.lieuFormation
          ? `${module.lieuFormation.voie} ${module.lieuFormation.codePostal} ${module.lieuFormation.commune}`
          : '',

        'Intitulé de formation': module?.intitule ?? '',
        'Date de début de formation': getDateIntitule(module?.dateDebut),
        'Date de fin de formation': getDateIntitule(module?.dateFin),
        'Nombre d’heure de formation': String(module?.duree),
        'Modalité pédagogique': module?.modalitePedagogique
          ? ModalitePedagogiqueLibelle[module.modalitePedagogique]
          : '',
        "Paiement direct à l'organisme de formation": getLabelBoolean(module.avecSubrogation!) ?? '',
        'Coût pédagogique': `${formatNumber(
          module?.fraisPedagogiques
            ?.filter((x) => x.nature === 'Pedagogique')
            .reduce((acc, frais) => acc + (frais.montant ?? 0), 0),
          { decimal: 2, separateur: true },
        )} €`,
        'Autres types de coûts': `${formatNumber((module?.fraisPedagogiques?.filter((x) => x.nature !== 'Pedagogique').reduce((acc, frais) => acc + (frais.montant ?? 0), 0) ?? 0) + (module?.fraisAnnexesSalarie?.reduce((acc, frais) => acc + (frais.montant ?? 0), 0) ?? 0), { decimal: 2, separateur: true })} €`,
      }))
  })

  // Détails du chiffrage
  const chiffrageDetails = computed(() => {
    const coutPedagogique = model?.formation?.modules
      ? model.formation.modules
          .flatMap((module) => module?.fraisPedagogiques ?? [])
          .reduce((acc, frais) => acc + (frais.montant ?? 0), 0)
      : 0

    const dureeFormation = model?.formation?.modules?.reduce((sum, mod) => sum + (mod.duree ?? 0), 0) ?? 0

    const idcc = model?.employeur?.etablissement?.idcc?.code ?? ''
    const typePublic = model?.typePublic

    const remuneration =
      model?.salaries?.reduce((sum, sal) => {
        const aCongesPayes =
          sal?.natureContrat === 'TravailTemporaire' && idccAutoriseTT.includes(idcc) && typePublic === 'Interimaire'

        const multiplicateur = aCongesPayes ? 1.1 : 1

        return sum + calculSalaire(sal.tauxHoraireBrut ?? 0, sal.pctTauxCharge ?? 0, dureeFormation) * multiplicateur
      }, 0) ?? 0

    const fraisAnnexes = model?.formation?.modules
      ? model.formation.modules
          .flatMap((module) => module?.fraisAnnexesSalarie ?? [])
          .reduce((acc, frais) => acc + (frais.montant ?? 0), 0)
      : 0

    const total = coutPedagogique + remuneration + fraisAnnexes

    return {
      'Frais de formation': `${formatNumber(coutPedagogique, { decimal: 2, separateur: true })} €`,
      Rémunération: `${formatNumber(remuneration, { decimal: 2, separateur: true })} €`,
      'Frais annexes': `${formatNumber(fraisAnnexes, { decimal: 2, separateur: true })} €`,
      Total: `${formatNumber(total, { decimal: 2, separateur: true })} €`,
    }
  })
</script>

<style scoped lang="less">
  .synthese__container {
    width: 90%;
    align-self: center;
    margin: @spacing-20 auto;
    display: flex;
    flex-wrap: wrap;
    justify-content: space-around;
    gap: 20px;

    > div {
      width: calc(45% - 10px);
      border-radius: @border-radius-l;

      @media (max-width: @breakpoint-tablet) {
        width: 100%;
      }
    }

    @container (max-width: @breakpoint-mobile) {
      width: 100%;
    }
  }

  .element {
    &__header {
      display: flex;
      font-weight: bold;
      padding-bottom: @spacing-10;
      border-bottom: 1px solid @secondary-600;
    }

    &__row {
      flex: 1;
      display: flex;
      padding: @spacing-5 0;
      border-bottom: 1px solid #e0e0e0;
    }

    &__header,
    &__row {
      > span {
        flex: 1;
        height: 20px;
        padding: 0 @spacing-5;
        text-align: left;
      }
    }
  }
</style>
